<template>
	<div>
		<div class="flex filter_table">
			<div class="flex flex_column w_100">
				<AgItem :columnDefs="columnDefs"
						:rowData="rowData"
						v-on:sortChanged="sortChanged"
				></AgItem>
				<Pagination
					:totalNum="totalNum"
					:pageSize="pageSize"
					:sizeChange="sizeChange"
					:currentChange="currentChange"
					:currentPage="page"
				></Pagination>
			</div>
			<RevolvingPutOptionContractOverviewFilter @apply-filter="applyFilter"/>
		</div>
		<RevolvingPutOptionPremiumPaymentsPlanModal ref="premiumPaymentsModal"/>
	</div>
</template>
<script>

import {asDecimal, asLocalizedDate, asMoneyAmount} from "@/components/templates/agGrid/formatter/valueFormatters";
import AgItem from "@/components/templates/agGrid/AgItem";
import {enumDef} from "@/common/enumDefs";
import Pagination from "@/components/templates/common/Pagination";
import PaginationParent from "@/components/templates/common/PaginationParent";
import RevolvingPutOptionContractOverviewFilter
	from "@/components/templates/seller/putOption/RevolvingPutOptionContractOverviewFilter";
import {revolvingPutOptionsSellerApi} from "@/common/api/revolvingPutOptionsSellerApi";
import RevolvingPutOptionPremiumPaymentsPlanModal
	from "@/components/templates/modal/revolvingPutOption/RevolvingPutOptionPremiumPaymentsPlanModal";

export default {
	extends: PaginationParent,
	components: {
		RevolvingPutOptionContractOverviewFilter,
		AgItem,
		Pagination,
		RevolvingPutOptionPremiumPaymentsPlanModal
	},
	data() {
		return {
			columnDefs: [],
			rowData: null,
			sorting: [],
			filters: [],
		};
	},
	beforeMount() {
		this.setColumnDefs();
		this.getList();
	},
	methods: {
		setColumnDefs() {
			this.columnDefs = [
				{
					headerName: this.$t("common.status"),
					field: "status",
					minWidth: 125,
					cellRendererFramework: "EnumAsText",
					enumDef: enumDef.revolvingPutOptionStatus,
					sortable: true,
				},
				{
					headerName: this.$t("common.debtor"),
					field: "debtorName",
					minWidth: 175,
					nameType: "debtorName",
					idType: "debtorId",
					cellRendererFramework: "CompanyTem",
					sortable: true
				},
				{
					headerName: this.$t("common.investor"),
					field: "ownerCompanyName",
					minWidth: 175,
					nameType: "ownerCompanyName",
					idType: "ownerCompanyId",
					cellRendererFramework: "CompanyTem",
					sortable: true
				},
				{
					headerName: this.$t("common.contractNumber"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.contractNumber.tooltip"),
					},
					field: "referenceNumber",
					minWidth: 175,
					cellRendererFramework: "RevolvingPutOptionReferenceRenderer",
					cellRendererParams: {
						soldReceivablesFunc: this.loadSoldReceivables
					},
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.optionStart"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.optionStart.tooltip"),
					},
					minWidth: 120,
					field: "startDate",
					valueFormatter: asLocalizedDate(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.optionEnd"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.optionEnd.tooltip"),
					},
					minWidth: 120,
					field: "endDate",
					valueFormatter: asLocalizedDate(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.discount"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.discount.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 150,
					field: "discountRate",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asDecimal({ showPercentSign: true }),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.premium"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.premium.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 150,
					field: "premium",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asDecimal({ showPercentSign: true }),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.revolvingLine"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.revolvingLine.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 150,
					field: "saleLimit",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asMoneyAmount(),
					sortable: true
				},
				{
					headerName: this.$t("common.ccy"),
					field: "currency",
					minWidth: 75,
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.utilization"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.utilization.tooltip"),
					},
					headerClass: "align-right",
					minWidth: 125,
					field: "usedSaleLimitPercentage",
					cellStyle: {'justify-content': 'flex-end'},
					valueFormatter: asDecimal({ showPercentSign: true }),
					sortable: true
				},
				{
					headerName: this.$t("common.actions"),
					minWidth: 100,
					cellRendererFramework: "RevolvingPutOptionContractOverviewTableOperation",
					cellRendererParams: {
						showPaymentPlan: this.showPaymentPlan,
						showReceivables: this.showReceivables,
						cancelOrdinary: this.confirmForOrdinaryCancel,
						cancelExtraordinary: this.confirmForExtraordinaryCancel,
					},
					pinned: "right",
				}
			];
		},
		sortChanged(sortData) {
			this.sorting = sortData;
			this.initPage();
			this.getList();
		},
		confirmForOrdinaryCancel(data) {
			let option = {
				title: this.$t("seller.sell.revolvingPutOptions.cancelOrdinary.confirm.title"),
				msg: this.$t("seller.sell.revolvingPutOptions.cancelOrdinary.confirm.msg"),
				confirmFun: this.cancelOptionOrdinary,
				params: data
			};
			this.confirmMeg(option, true);
		},
		cancelOptionOrdinary(data) {
			this.cancelOption(data, true);
		},
		confirmForExtraordinaryCancel(data) {
			let option = {
				title: this.$t("seller.sell.revolvingPutOptions.cancelExtraordinary.confirm.title"),
				msg: this.$t("seller.sell.revolvingPutOptions.cancelExtraordinary.confirm.msg", {
					TBCareMail: this.$store.getters.careEmail
				}),
				confirmFun: this.cancelOptionExtraordinary,
				params: data
			};
			this.confirmMeg(option, true);
		},
		cancelOptionExtraordinary(data) {
			this.cancelOption(data, false);
		},
		cancelOption(data, ordinary) {
			revolvingPutOptionsSellerApi.operation.cancelPutOption(data.id, ordinary)
				.then(() => {
					this.toastSuccess();
					this.$emit("changed");
					this.getList();
				})
				.catch(this.toast);
		},
		getList() {
			let postData = {
				columns: [],
				filters: this.filters || [],
				maxResults: this.maxResults,
				offset: this.offset,
				sorting: this.sorting
			};

			revolvingPutOptionsSellerApi.inspection.listContracts(postData)
				.then(res => {
					this.setTotalNum(res.recordsTotal);
					this.rowData = res.data;
				})
				.catch(this.toast);
		},
		showPaymentPlan(data) {
			this.$refs.premiumPaymentsModal.showWithData(
				data.referenceNumber,
				revolvingPutOptionsSellerApi.inspection.getPaymentPlan(data.id)
			);
		},
		showReceivables(data) {
			this.$store.dispatch("setActionNoticModal", {
				show: true,
				data: {
					title: data.referenceNumber,
					type: "RevolvingPutOptionContractDetailsModal",
					width: 600,
					data: {
						revolvingPutOption: data,
						soldReceivablesFunc: this.loadSoldReceivables
					}
				}
			});
		},
		loadSoldReceivables(data, postData) {
			return revolvingPutOptionsSellerApi.inspection.listReceivables(data.id, postData);
		},
		applyFilter(filters) {
			this.filters = filters;
			this.getList();
		},
	},
};
</script>
